/** Where the marks and promised windows sit on an incident's timeline: a wall-clock axis in percent, sized from the promise. */

import { IncidentView, SlaTarget } from './incidents';
import { deadlineAfterPauses, pauseSpans, pausedMsBetween, workingMsBetween } from './incidentPauses';

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;

/** Room after the last promise runs out, so the deadline is not the last thing on the axis. */
const TAIL_HOURS = 3;

/** That day's midnight plus `hours`, which rolls into the next day past 24 and survives a DST change. */
const atHour = (ms: number, hours: number): number => {
  const at = new Date(ms);
  at.setHours(hours, 0, 0, 0);
  return at.getTime();
};

/** Room before the incident, so it does not come in on the axis's own edge. */
const LEAD_HOURS = 1;

/** The least lead a sliding window leaves itself, so the chevron still draws clear of the edge. */
const MIN_LEAD_HOURS = 0.5;

/** Whole steps at or above this many hours. */
const ceilToStepHours = (hours: number, stepHours: number): number => Math.ceil(hours / stepHours) * stepHours;

/** The axis width for a promise of this length, from the promise alone, so an hour is the same width on every card carrying it. */
const spanHoursFor = (promiseHours: number, stepHours: number): number =>
  ceilToStepHours(LEAD_HOURS + stepHours + promiseHours + TAIL_HOURS + stepHours, stepHours);

/** The last grid line at or before this moment; past a day the grid anchors on the axis's own midnight. */
const floorToStep = (ms: number, stepHours: number): number => {
  const hours = new Date(ms).getHours();
  return atHour(ms, stepHours >= 24 ? 0 : Math.floor(hours / stepHours) * stepHours);
};

/** The window slid to catch a later moment, keeping its width and moving in whole ticks; what it cannot reach keeps a marker. */
const slide = (
  bounds: { fromMs: number; toMs: number },
  startMs: number,
  lastMomentMs: number,
  stepHours: number,
): { fromMs: number; toMs: number } => {
  if (lastMomentMs <= bounds.toMs) return bounds;

  const stepMs = stepHours * HOUR;
  const wanted = Math.ceil((lastMomentMs - bounds.toMs) / stepMs);
  // What the lead can pay for, spending it down to the floor and no further.
  const affordable = Math.floor((startMs - bounds.fromMs - MIN_LEAD_HOURS * HOUR) / stepMs);
  // All of it or none: a partial slide spends the lead and still draws the marker.
  if (wanted < 1 || wanted > affordable) return bounds;

  const by = (ms: number): number => atHour(ms, new Date(ms).getHours() + wanted * stepHours);
  return { fromMs: by(bounds.fromMs), toMs: by(bounds.toMs) };
};

/** How far apart the ticks go: an hour up to half a day, then days, so the labels stay readable. */
export const tickStepHours = (spanHours: number): number => {
  if (spanHours <= 12) return 1;
  if (spanHours <= 24) return 2;
  if (spanHours <= 72) return 6;
  if (spanHours <= 168) return 12;
  return 24 * Math.ceil(spanHours / (12 * 24));
};

/** The hours to draw: the head snapped onto the tick grid, then a fixed width, settled by iterating since step and width define each other. */
export const pickWindow = (
  startMs: number,
  lastPromiseMs: number,
  /** The last thing there is to draw — resolved, or how far an open incident has got. */
  lastMomentMs: number = lastPromiseMs,
): { fromMs: number; toMs: number } => {
  const promiseHours = (lastPromiseMs - startMs) / HOUR;
  let step = 1;
  let bounds = { fromMs: startMs, toMs: lastPromiseMs };

  // Four is room for the step to climb the whole ladder, however the agreement is rewritten.
  for (let pass = 0; pass < 4; pass += 1) {
    const span = spanHoursFor(promiseHours, step);
    const fromMs = floorToStep(startMs - LEAD_HOURS * HOUR, step);
    bounds = { fromMs, toMs: atHour(fromMs, new Date(fromMs).getHours() + span) };

    const settled = tickStepHours(span);
    if (settled === step) break;
    step = settled;
  }

  return slide(bounds, startMs, lastMomentMs, step);
};

export interface TimelineMark {
  /** The instant it happened. */
  atMs: number;
  /** Where it sits along the drawn axis, 0–100. */
  at: number;
  /** Minutes from when the incident came in, for the elapsed figures beside it. */
  minutes: number;
  /** Past the drawn axis: `at` is 100, a position and not a time, so it earns a marker rather than a line. */
  offWindow: boolean;
}

export interface TimelineWindow {
  /** How long the promise was, in minutes. */
  minutes: number;
  /** The instant the promise runs out. */
  atMs: number;
  /** Where that falls on the drawn axis, 0–100. */
  at: number;
  /** Whether the moment this window promised was actually met. */
  met: boolean;
  /** Minutes over the promise, or null when it was kept. */
  overBy: number | null;
  /** Minutes the promise actually took, counted from the incident coming in. */
  elapsed: number;
  /** The promise outlasts the drawn axis, so the bar is cut off rather than ending where it does. */
  clamped: boolean;
}

/** A stretch of the axis that was somebody else's to move, held as a position so band and promise share one axis. */
export interface TimelineBand {
  atMs: number;
  /** Where it starts and ends on the drawn axis, 0-100. */
  from: number;
  to: number;
  /** Minutes it covers, for the key underneath. */
  minutes: number;
  reason: string | null;
}

export interface TimelineTick {
  atMs: number;
  at: number;
  /** Midnight, which is where the date rather than the hour is worth printing. */
  isMidnight: boolean;
}

export interface IncidentTimeline {
  fromMs: number;
  toMs: number;
  /** Where the incident came in. Not 0 any more: the axis starts at an hour, not at the incident. */
  start: TimelineMark;
  /** Answered, or null while nobody has. */
  responded: TimelineMark | null;
  /** Where the incident currently stands: the moment it closed, or now while it is still running. */
  end: TimelineMark;
  /** Whether `end` is the resolution or just how far it has got. */
  isOpen: boolean;
  /** The promise to answer, judged by the clock while nobody has answered yet. */
  responseWindow: TimelineWindow;
  resolutionWindow: TimelineWindow;
  ticks: TimelineTick[];
  /** The stretches nothing was ours to do, drawn over the resolve promise, which is the one they moved. */
  pauses: TimelineBand[];
  /** Minutes of the whole picture spent waiting, which is what the key says in words. */
  pausedMinutes: number;
}

const clamp = (value: number): number => Math.max(0, Math.min(100, value));

/** One incident's timeline. `now` is taken rather than read, so the whole page agrees what now is. */
export const buildTimeline = (incident: IncidentView & { target: SlaTarget }, now = new Date()): IncidentTimeline => {
  const startMs = Date.parse(incident.reportedAt);
  const endMs = incident.resolvedAt ? Date.parse(incident.resolvedAt) : Math.max(startMs, now.getTime());

  // Merged, and a wait nobody ended stops where the incident does, not where the page was opened.
  const spans = pauseSpans(incident.pauses ?? [], endMs);

  // The later promise decides the far end, taken where waiting pushed it, or a paused incident draws it off the edge.
  const lastPromiseMs = deadlineAfterPauses(
    startMs,
    startMs + Math.max(incident.target.responseHours, incident.target.resolutionHours) * HOUR,
    spans,
  );
  // The answer is taken in too: nothing stops it being recorded after the resolution.
  const lastMomentMs = Math.max(endMs, incident.respondedAt ? Date.parse(incident.respondedAt) : startMs);
  const { fromMs, toMs } = pickWindow(startMs, lastPromiseMs, lastMomentMs);
  const span = toMs - fromMs;

  const at = (ms: number): number => clamp(((ms - fromMs) / span) * 100);
  const mark = (ms: number): TimelineMark => ({
    atMs: ms,
    at: at(ms),
    minutes: Math.round((ms - startMs) / MINUTE),
    offWindow: ms > toMs,
  });

  // `stops` is a fact about a promise: the deadline and the elapsed figure lose the same stretches, so they agree.
  const window = (targetHours: number, actualMs: number | null, stops = false): TimelineWindow => {
    const targetMs = startMs + targetHours * HOUR;
    const dueMs = stops ? deadlineAfterPauses(startMs, targetMs, spans) : targetMs;
    const against = actualMs ?? endMs;
    const elapsedMs = stops ? workingMsBetween(spans, startMs, against) : against - startMs;

    return {
      minutes: Math.round(targetHours * 60),
      atMs: dueMs,
      at: at(dueMs),
      // The promise is broken by the clock, not by the record.
      met: against <= dueMs,
      overBy: against > dueMs ? Math.round((against - dueMs) / MINUTE) : null,
      elapsed: Math.round(elapsedMs / MINUTE),
      clamped: dueMs > toMs,
    };
  };

  // Ticks counted from midnight, so a six-hourly axis marks 00:00 and 06:00, not the arrival hour.
  const step = tickStepHours(span / HOUR);
  const ticks: TimelineTick[] = [];
  for (let cursor = new Date(atHour(fromMs, 0)); cursor.getTime() <= toMs; cursor.setHours(cursor.getHours() + step)) {
    const tickMs = cursor.getTime();
    if (tickMs >= fromMs) ticks.push({ atMs: tickMs, at: at(tickMs), isMidnight: cursor.getHours() === 0 });
  }

  return {
    fromMs,
    toMs,
    start: mark(startMs),
    responded: incident.respondedAt ? mark(Date.parse(incident.respondedAt)) : null,
    end: mark(endMs),
    isOpen: incident.resolvedAt === null,
    responseWindow: window(
      incident.target.responseHours,
      incident.respondedAt ? Date.parse(incident.respondedAt) : null,
      true,
    ),
    resolutionWindow: window(
      incident.target.resolutionHours,
      incident.resolvedAt ? Date.parse(incident.resolvedAt) : null,
      true,
    ),
    ticks,
    // Clipped to what is drawn, so a wait that began before the axis starts is the part of it on screen.
    pauses: spans.map(pause => ({
      atMs: pause.fromMs,
      from: at(pause.fromMs),
      to: at(pause.toMs),
      minutes: Math.round((pause.toMs - pause.fromMs) / MINUTE),
      reason: (incident.pauses ?? []).find(entry => Date.parse(entry.startedAt) === pause.fromMs)?.reason ?? null,
    })),
    pausedMinutes: Math.round(pausedMsBetween(spans, startMs, endMs) / MINUTE),
  };
};
